import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { X } from "lucide-react"
import { toast } from "./Toast"
import { AddressFields } from "./AddressFields"
import {
  kontaktArtLabel, erlaubteArtWechsel, artIstAenderbar,
} from "@/lib/kontaktArten"

type HeroContactDetail = {
  id: number
  salutation?: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone_mobile?: string
  phone_home?: string
  street?: string
  postcode?: string
  city?: string
  kunden_slug?: string
  is_contact_person?: boolean
  is_invoice_recipient?: boolean
  category?: string
  category_name?: string
  error?: string
}

/**
 * Kontakt-Edit-Modal — 1:1-Port von sgEditContact aus admin/sipgate.py.
 * Laedt Kontakt ueber /admin/contacts/api/{id}?tenant=X (lokaler Cache oder HERO-Fallback),
 * schreibt Aenderungen via /admin/contacts/api/{id}/update (HERO Mutation + Cache-Update).
 */
export function ContactEditModal({
  open,
  onClose,
  contactId,
  tenantSlug,
  invalidateKeys,
}: {
  open: boolean
  onClose: () => void
  contactId: number | null
  tenantSlug: string
  invalidateKeys?: (string | number)[][]
}) {
  const qc = useQueryClient()
  const [sal, setSal] = useState("")
  const [first, setFirst] = useState("")
  const [last, setLast] = useState("")
  const [company, setCompany] = useState("")
  const [email, setEmail] = useState("")
  const [phoneMobile, setPhoneMobile] = useState("")
  const [phoneHome, setPhoneHome] = useState("")
  const [street, setStreet] = useState("")
  const [zipcode, setZipcode] = useState("")
  const [city, setCity] = useState("")
  const [rechnungsempfaenger, setRechnungsempfaenger] = useState(false)
  // Art des Kontakts (#50). Leer = unveraendert lassen: ein Ansprechpartner
  // („contact") steht nicht zur Auswahl, und ihn beim Speichern still auf
  // „Kunde" umzubiegen waere schlimmer als ihn zu lassen.
  const [art, setArt] = useState("")
  // Was HERO heute fuehrt — daraus ergibt sich, was ueberhaupt waehlbar ist.
  const [urspruenglicheArt, setUrspruenglicheArt] = useState("")
  const [err, setErr] = useState<string | null>(null)

  const detail = useQuery<HeroContactDetail>({
    queryKey: ["contact-detail", contactId, tenantSlug],
    queryFn: async () => {
      const url = `/admin/contacts/api/${contactId}${tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : ""}`
      const res = await fetch(url, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!contactId,
    staleTime: 0,
  })

  useEffect(() => {
    if (open && detail.data && !detail.data.error) {
      const d = detail.data
      setSal(d.salutation || "")
      setFirst(d.first_name || "")
      setLast(d.last_name || "")
      setCompany(d.company_name || "")
      setEmail(d.email || "")
      setPhoneMobile(d.phone_mobile || "")
      setPhoneHome(d.phone_home || "")
      setStreet(d.street || "")
      setZipcode(d.postcode || "")
      setCity(d.city || "")
      setRechnungsempfaenger(!!d.is_invoice_recipient)
      setArt((d.category || "").toLowerCase())
      setUrspruenglicheArt((d.category || "").toLowerCase())
      setErr(null)
    }
  }, [open, detail.data])

  const save = useMutation({
    mutationFn: async () => {
      const slug = tenantSlug || detail.data?.kunden_slug || ""
      const res = await fetch(`/api/v1/contacts/${contactId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          tenant_slug: slug,
          salutation: sal,
          first_name: first.trim(),
          last_name: last.trim(),
          company_name: company.trim(),
          phone_mobile: phoneMobile.trim(),
          phone_home: phoneHome.trim(),
          email: email.trim(),
          street: street.trim(),
          zipcode: zipcode.trim(),
          city: city.trim(),
          is_invoice_recipient: rechnungsempfaenger,
          // Nur senden, wenn sie sich wirklich aendert: HERO lehnt jeden
          // anderen Wechsel ab und laesst dann die GANZE Mutation scheitern.
          ...(art && art !== urspruenglicheArt ? { category: art } : {}),
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: () => {
      toast("✓ Kontakt aktualisiert")
      ;(invalidateKeys || []).forEach((k) => qc.invalidateQueries({ queryKey: k }))
      qc.invalidateQueries({ queryKey: ["contact-detail", contactId] })
      onClose()
    },
    onError: (e) => {
      const msg = e instanceof Error ? e.message : String(e)
      setErr(msg)
      toast("Speichern fehlgeschlagen: " + msg, "error")
    },
  })

  if (!open) return null

  const submit = (ev: React.FormEvent) => {
    ev.preventDefault()
    setErr(null)
    if (!last.trim() && !company.trim()) { setErr("Nachname oder Firma ist Pflichtfeld"); return }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setErr("Ungültige E-Mail"); return }
    save.mutate()
  }

  const displayName = `${first} ${last}`.trim() || company.trim() || `Kontakt #${contactId}`

  return (
    <div
      className="pp-overlay oben"
      data-portal-modal
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.5)",
               display: "flex", alignItems: "flex-start", justifyContent: "center",
               padding: "24px 16px", overflowY: "auto" }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,.25)",
          width: "100%", maxWidth: 520,
          display: "flex", flexDirection: "column",
          margin: "auto",
        }}
      >
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <h2 className="text-[15px] font-semibold">
            Kontakt bearbeiten: {displayName}
          </h2>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 overflow-y-auto space-y-3">
          {detail.isLoading && (
            <div className="text-center py-8 text-muted-foreground text-[13px]">Lädt...</div>
          )}
          {detail.data?.error && (
            <div className="pp-alert pp-alert-err">Fehler: {detail.data.error}</div>
          )}
          {detail.isError && (
            <div className="pp-alert pp-alert-err">Fehler beim Laden: {String(detail.error)}</div>
          )}
          {err && <div className="pp-alert pp-alert-err">{err}</div>}
          {!detail.isLoading && !detail.data?.error && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <label className="pp-form-label">Anrede</label>
                  <select value={sal} onChange={(e) => setSal(e.target.value)}>
                    <option value="">--</option>
                    <option value="Herr">Herr</option>
                    <option value="Frau">Frau</option>
                    <option value="Divers">Divers</option>
                  </select>
                </div>
                <div />
                <div>
                  <label className="pp-form-label">Vorname</label>
                  <input value={first} onChange={(e) => setFirst(e.target.value)} />
                </div>
                <div>
                  <label className="pp-form-label">Nachname</label>
                  <input value={last} onChange={(e) => setLast(e.target.value)} />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <label className="pp-form-label">Firma</label>
                  <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="z.B. Muster GmbH" />
                </div>
                <div>
                  <label className="pp-form-label">Mobil</label>
                  <input value={phoneMobile} onChange={(e) => setPhoneMobile(e.target.value)} placeholder="z.B. +49176..." />
                </div>
                <div>
                  <label className="pp-form-label">Festnetz</label>
                  <input value={phoneHome} onChange={(e) => setPhoneHome(e.target.value)} placeholder="z.B. +49721..." />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <label className="pp-form-label">E-Mail</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div style={{ gridColumn: "span 2" }}>
                  <AddressFields
                    labelStrasse="Straße"
                    wert={{ street, zip: zipcode, city }}
                    onChange={(w) => { setStreet(w.street); setZipcode(w.zip); setCity(w.city) }}
                  />
                </div>
                {/* Art des Kontakts — bis #50 gab es das Feld nicht, und der
                    Typ liess sich nach dem Anlegen nirgends mehr aendern. */}
                <div style={{ gridColumn: "span 2" }}>
                  <label className="pp-form-label">Art des Kontakts</label>
                  {artIstAenderbar(urspruenglicheArt) ? (
                    <select value={art} onChange={(e) => setArt(e.target.value)}>
                      {erlaubteArtWechsel(urspruenglicheArt).map((wert) => (
                        <option key={wert} value={wert}>
                          {kontaktArtLabel(wert)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    /* Kein Auswahlfeld, das beim Speichern scheitern wuerde:
                       HERO laesst die Art nachtraeglich nicht mehr aendern. */
                    <>
                      <div style={{
                        padding: "7px 10px", border: "1px solid var(--border-color)",
                        borderRadius: 6, fontSize: 13, background: "var(--bg-muted, #f8fafc)",
                      }}>
                        {kontaktArtLabel(urspruenglicheArt, detail.data?.category_name)
                          || "– unbekannt –"}
                      </div>
                      <div style={{ fontSize: 11.5, color: "var(--text-muted)", marginTop: 4 }}>
                        HERO lässt die Art nach dem Anlegen nicht mehr ändern.
                      </div>
                    </>
                  )}
                </div>
                {/* HERO-Rolle, kein Portal-Feld: gesetzt biegt HERO die
                    Rechnungsanschrift auf diesen Kontakt um. Fuer den Fall
                    „Mieter ist Ansprechpartner, Rechnung an den Vermieter". */}
                <div style={{ gridColumn: "span 2" }}>
                  <label
                    style={{ display: "flex", gap: 8, alignItems: "flex-start", cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={rechnungsempfaenger}
                      onChange={(e) => setRechnungsempfaenger(e.target.checked)}
                      style={{ marginTop: 2, width: "auto" }}
                    />
                    <span>
                      <span style={{ fontSize: 13, fontWeight: 500 }}>Rechnungsempfänger</span><br />
                      <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        HERO schreibt die Rechnung an diesen Kontakt, auch wenn ein anderer
                        Hauptkontakt ist. Die Anrede im Brief zieht nicht mit.
                      </span>
                    </span>
                  </label>
                </div>
              </div>
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between">
          {contactId && tenantSlug && (
            <a
              href={`/admin/open-hero/contact/${contactId}?tenant=${encodeURIComponent(tenantSlug)}`}
              target="_blank"
              rel="noreferrer"
              className="text-[12px] text-primary hover:underline"
            >
              In HERO ↗
            </a>
          )}
          <div className="flex gap-2 ml-auto">
            <button type="button" onClick={onClose} className="pp-btn pp-btn-sm">Abbrechen</button>
            <button
              type="submit"
              disabled={save.isPending || detail.isLoading}
              className="pp-btn pp-btn-primary pp-btn-sm"
            >
              {save.isPending ? "Speichert..." : "Speichern"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}
